import React, { FC } from 'react'
import { breakpoint, styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { Text, Flex } from '@moonpig/launchpad-components'
import {
  IconSystemCornerDownRight,
  IconSystemSearch,
} from '@moonpig/launchpad-assets'
import { DepartmentsEnum } from '@moonpig/web-core-types-graphql'
import { ThemeInterface } from '@moonpig/launchpad-theme'
import { SearchFallbackItemType } from '../types'
import { useLocaleText } from '../../locale'
import { useScrollIntoViewOnHighlight } from '../hooks/ScrollIntoViewOnHighlight'

type FallbackSearchOptionProps = {
  department: { enum: DepartmentsEnum; name: string; title: string }
  searchTerm: string
  getItemProps: (props: {
    itemIndex: number
    item: SearchFallbackItemType
  }) => Record<string, unknown>
  highlighted: boolean
}

type StyledFallbackSearchOptionProps = {
  highlighted?: boolean
  theme: ThemeInterface
}

const ICON_SIZE_PX = '20px'

const StyledFallbackSearchOption = styled(
  Flex,
)<StyledFallbackSearchOptionProps>`
  ${s({
    p: 3,
    m: 3,
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 2,
  })}
  cursor: pointer;

  ${({ highlighted }) =>
    s({
      bgcolor: highlighted ? 'colorBackground02 ' : 'colorBackground01',
    })}

  &:hover,
  &:focus,
  &:focus-visible {
    ${s({
      bgcolor: 'colorBackground02',
    })}
  }

  &:active {
    ${s({
      bgcolor: 'colorBackgroundSelectedState',
    })}
  }
`

const StyledText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
`

const StyledButtonText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;

  ${s({
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: '20px',
    color: 'colorInteractionControl',
  })}
`

const StyledSearchIconWrapper = styled(Flex)`
  ${s({
    bgcolor: 'colorBackground01',
    borderRadius: 1,
    p: 4,
    color: 'colorInteractionControl',
  })}
`

const StyledCornerDownIconWrapper = styled(Flex)`
  ${s({
    bgcolor: 'colorBackground01',
    color: 'colorInteractionControl',
  })}
`

const StyleContainer = styled(Flex)`
  ${s({
    width: '100%',
    gap: '8px;',
  })}
`

const EnterButton = styled.div`
  display: none;

  ${breakpoint('md')} {
    display: flex;
    align-items: center;
    justify-content: center;

    ${s({
      borderColor: 'colorBorder03',
      bgcolor: 'colorPrimary03',
      borderRadius: 1,
      padding: 4,
      border: 1,
      gap: 4,
      width: '86px',
    })}
  }
`

export const FallbackSearchOption: FC<FallbackSearchOptionProps> = ({
  department,
  searchTerm,
  getItemProps,
  highlighted,
}) => {
  const t = useLocaleText()

  const text = t('search.search_for_with_term', {
    searchTerm,
  })
  const enterButtonText = t('search.enter').toLocaleUpperCase()
  const fallbackOptionRef = useScrollIntoViewOnHighlight<HTMLDivElement>(
    !!highlighted,
  )

  return (
    <StyledFallbackSearchOption
      ref={fallbackOptionRef}
      {...getItemProps({
        itemIndex: 0,
        item: { value: searchTerm, itemType: 'search-fallback', department },
      })}
      highlighted={highlighted}
      tabIndex={0}
      data-testid="search-fallback-option"
      data-searchterm={searchTerm}
      data-itemtype="search-fallback"
      data-department={department.enum}
    >
      <StyleContainer>
        <StyledSearchIconWrapper>
          <IconSystemSearch width={ICON_SIZE_PX} height={ICON_SIZE_PX} />
        </StyledSearchIconWrapper>
        <StyledText typography="typeBodyText" color="colorTextLabel">
          {text}
        </StyledText>
      </StyleContainer>

      <EnterButton>
        <StyledCornerDownIconWrapper>
          <IconSystemCornerDownRight
            width={ICON_SIZE_PX}
            height={ICON_SIZE_PX}
          />
        </StyledCornerDownIconWrapper>
        <StyledButtonText typography="typeButtonLabel">
          {enterButtonText}
        </StyledButtonText>
      </EnterButton>
    </StyledFallbackSearchOption>
  )
}
